import React, { memo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DataLayer from '../../DataLayer/DataLayer';
import FeatureDOM from '../../DataLayer/FeatureDOM';
import { Box } from '@mui/material';
import { getLayerConfig } from '../../Prescription/PrescriptionUtils';
import { useLangFile } from '../../../context/LanguageContext';
import {
  renderCustomDemandLegend,
  renderStandardDemandLegend,
} from '../../DemandScale/DemandScale';
import useMapEvent from '../../DataLayer/hooks/useMapEvent';
import { useGoogleMap } from '../../../context/GoogleMapContext';
import MapCanvas from './MapCanvas';
import * as turf from '@turf/turf';
import { voidFunc } from '../../../constants/PropTypeUtils';
import ViewModeConstants from '../../../constants/ViewModeConstants';
import {
  ClassificationColoringStrategy,
  getColoringStrategy,
} from '../../../model/surveys/SurveyColoringStrategies';
import { SATELLITE_LAYERS } from '../../../constants/SatelliteLayers';
import { SURVEY_LAYERS } from '../../../constants/SurveyLayers';
import { truncateNumber } from '../../../helpers/NumberUtils';
import { hasSoilClassLabels } from '../../../model/surveys/country/SurveyCountryStrategy';
import { FINE_MAPPINGS_KEY } from '../../../model/surveys/SurveyImageMapping';
import { formatSignedRt } from '../../../helpers/RtDiffFormat';

export const getDemandTooltip = (
  layer,
  allValues,
  target,
  classificationValues,
  classificationsEnabled,
  variationsEnabled,
  LangFile,
  countryCode
) => {
  const decimals = 2;

  const values = allValues[layer];
  const config = getLayerConfig(layer, countryCode);
  let text = `${config.getName(LangFile)}`;
  let short = config.getShorthand(LangFile);
  if (short) {
    text += ` (${short})`;
  }
  text += ':';

  let result = { layer: layer, value: null, text: text, icon: null, unit: config.unit };

  const isDkRt = layer === SURVEY_LAYERS.RT && countryCode === 'DK';

  if (values && target && target.x >= 0 && target.y >= 0) {
    const { x, y } = target;
    const col = values[y];
    const row = col && col[x];
    const val = row && truncateNumber(row, decimals);

    if (val != null) {
      if (classificationsEnabled) {
        if (isDkRt) {
          // DK Rt classifies against RT_DIFF in diff units, so we need to look up the diff value and classify against that.
          // Currently this a unique approach in DK, thus only applies to DK RT, so if we ever need to add more layers that
          // classify against diffs values we should consider a more generic approach to handling this in the classification strategies.
          const diffCol = allValues[SURVEY_LAYERS.RT_DIFF];
          const diffVal = diffCol && diffCol[y] && diffCol[y][x];
          if (typeof diffVal === 'number' && Number.isFinite(diffVal)) {
            const classifier = new ClassificationColoringStrategy(
              layer,
              ViewModeConstants.ANALYSIS,
              countryCode
            ).classifier;
            const classification = classifier(diffVal);
            if (classification) {
              result.icon = renderStandardDemandLegend(classification, LangFile, layer);
            }
          }
        } else if (classificationValues[layer]) {
          const classifications = classificationValues[layer]['values'];
          const classificationCol = classifications && classifications[y];
          const classificationValue = classificationCol && classificationCol[x];
          const mappings = classificationValues[layer]['mappings'];
          if (classificationValue != null && mappings && mappings[FINE_MAPPINGS_KEY]) {
            const classificationLegends = mappings[FINE_MAPPINGS_KEY];
            const classification = Object.keys(classificationLegends).find((key) =>
              classificationLegends[key].includes(classificationValue)
            );
            if (classification) {
              result.icon = renderCustomDemandLegend(classification, LangFile);
            }
          }
        }
      }

      if (hasSoilClassLabels(layer)) {
        result.value = getColoringStrategy(
          layer,
          ViewModeConstants.ANALYSIS,
          classificationsEnabled,
          variationsEnabled,
          values,
          countryCode
        ).getLabel(val);
      } else {
        result.value = val;
      }
    }
  }

  const rows = [result];

  // DK Rt + demand on: append a second and third row showing desired Rt + signed diff.
  // This is a unique case for DK RT, so we handle it directly here in the tooltip generation rather than trying
  // to shoehorn it into the more generic classification strategy logic which is shared across multiple layers and countries.
  if (isDkRt && classificationsEnabled && target && target.x >= 0 && target.y >= 0) {
    const desiredCol = allValues[SURVEY_LAYERS.RT_DESIRED];
    const diffCol = allValues[SURVEY_LAYERS.RT_DIFF];
    const desired = desiredCol && desiredCol[target.y] && desiredCol[target.y][target.x];
    const diff = diffCol && diffCol[target.y] && diffCol[target.y][target.x];

    if (
      typeof desired === 'number' &&
      Number.isFinite(desired) &&
      typeof diff === 'number' &&
      Number.isFinite(diff)
    ) {
      const signedDiff = formatSignedRt(diff);
      rows.push({
        layer: SURVEY_LAYERS.RT_DESIRED,
        value: `${desired.toFixed(2)}`,
        text: `${LangFile.SurveyLayers.rtDesired}:`,
        icon: null,
        unit: '',
      });
      rows.push({
        layer: SURVEY_LAYERS.RT_DIFF,
        value: signedDiff,
        text: `${LangFile.SurveyLayers.rtDiff}:`,
        icon: null,
        unit: '',
      });
    }
  }

  return rows;
};

const MapCanvasDemandTooltip = (props: MapCanvasDemandTooltip.propTypes) => {
  const LangFile = useLangFile();
  const googleMap = useGoogleMap();
  const contextRef = useRef(null);

  const [target, setTarget] = useState(null);

  // Handles setting the reference to the context on which to draw the hover effect.
  const onHoverCanvasDraw = useCallback((ctx) => {
    if (!contextRef.current) {
      contextRef.current = ctx;
    }
  }, []);

  // Handles mapping from map to canvas space.
  const latLngToPixel = useCallback(
    (lat, lng, allowOutOfBounds) => {
      const bounds = props.selectedField.bounds;
      const { width, height } = props.canvasSize;

      // Figure out where in the image we are clicking (0 to 1)
      const { north, south, east, west } = bounds;

      const latRange = north - south;
      const lngRange = east - west;
      const lngPartial = (lng - west) / lngRange;
      const latPartial = (north - lat) / latRange;

      // Convert this to the x and y coordinates in the canvas
      let x = Math.round(width * lngPartial);
      let y = Math.round(height * latPartial);

      if (!allowOutOfBounds) {
        x = Math.min(width - 1, x);
        x = Math.max(0, x);
        y = Math.min(height - 1, y);
        y = Math.max(0, y);
      }

      return { x, y };
    },
    [props.selectedField, props.canvasSize]
  );

  // Event handler for "mousemove" events on the map that is translated into the canvas hover effect.
  const mousemoveHandler = useCallback(
    (event) => {
      let ctx = contextRef.current;

      if (!ctx) {
        return;
      }

      let s = props.resolutionScale;
      let width = props.canvasSize.width * s;
      let height = props.canvasSize.height * s;
      let prevFill = ctx.fillStyle;
      ctx.clearRect(0, 0, width, height);

      let latLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      let point = turf.point([latLng.lng, latLng.lat]);
      let { x, y } = latLngToPixel(latLng.lat, latLng.lng, true);

      let borderPoints = props.selectedField.polygon.coordinates[0];
      let border = turf.polygon([borderPoints]);

      if (turf.booleanContains(border, point)) {
        let t = { ...latLng, x, y };

        setTarget(t);
        props.onTargetChanged(t);

        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(x * s, y * s, s, s);
        ctx.fillStyle = prevFill;
      } else {
        setTarget(null);
        props.onTargetChanged(null);
      }
    },
    [props.selectedField, props.resolutionScale, props.onTargetChanged]
  );

  // Register mousemove event handler on the map
  useMapEvent(googleMap, 'mousemove', mousemoveHandler);

  let items =
    props.layers
      .flatMap((layer, idx, arr) => {
        // Don't show tooltip for VISIBLE (NDVI) layer.
        // Don't show tooltip for VARIATIONS if the VITALITY is also shown.
        if (
          layer === SATELLITE_LAYERS.VISIBLE ||
          (layer === SATELLITE_LAYERS.VARIATIONS_NDVI &&
            arr.indexOf(SATELLITE_LAYERS.VITALITY_NDVI) !== -1)
        ) {
          return [];
        }
        return getDemandTooltip(
          layer,
          props.values,
          target,
          props.classificationValues,
          props.classificationsEnabled,
          props.variationsEnabled,
          LangFile,
          props.countryCode
        );
      })
      .filter(Boolean)
      .filter((item) => item.value !== undefined && item.value !== null) || [];

  return (
    <>
      <MapCanvas
        onDraw={onHoverCanvasDraw}
        zIndex={3}
        bounds={props.selectedField.bounds}
        width={props.canvasSize.width * props.resolutionScale}
        height={props.canvasSize.height * props.resolutionScale}
      />

      <DataLayer>
        {target && items.length > 0 && (
          <FeatureDOM style={{ pointerEvents: 'none' }} lat={target.lat} lng={target.lng}>
            <Box
              style={{ userSelect: 'none', pointerEvents: 'none' }}
              sx={{
                position: 'relative',
              }}
            >
              <Box
                style={{ transform: 'translate(24px, -24px)' }}
                sx={{
                  position: 'absolute',
                }}
              >
                <Box
                  style={{ opacity: 0.7 }}
                  sx={{
                    py: 0.5,
                    px: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'white',
                    bgcolor: 'black',
                    flexWrap: 'nowrap',
                    borderRadius: 4,
                  }}
                >
                  {items.map(({ layer, value, text, icon, unit }) => {
                    let showIcon = props.classificationsEnabled && Boolean(icon);
                    let classIcon = showIcon && icon;
                    return (
                      <Box
                        key={layer}
                        sx={{
                          py: 0.5,
                          display: 'inline-flex',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box style={{ whiteSpace: 'nowrap' }}>{text}</Box>
                        <Box
                          style={{ whiteSpace: 'nowrap' }}
                          sx={{
                            pl: 1,
                            height: '100%',
                            alignItems: 'center',
                            display: 'flex',
                          }}
                        >
                          {value}
                          {unit}
                          <span style={{ paddingLeft: 4 }} />
                          {classIcon}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </FeatureDOM>
        )}
      </DataLayer>
    </>
  );
};

MapCanvasDemandTooltip.propTypes = {
  layers: PropTypes.array,
  values: PropTypes.object,
  classificationValues: PropTypes.object,
  selectedField: PropTypes.object,
  canvasSize: PropTypes.object,
  classificationsEnabled: PropTypes.bool,
  variationsEnabled: PropTypes.bool,
  resolutionScale: PropTypes.number,
  onTargetChanged: PropTypes.func,
  countryCode: PropTypes.string,
};

MapCanvasDemandTooltip.defaultProps = {
  onTargetChanged: voidFunc,
};

export default memo(MapCanvasDemandTooltip);
